import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Phone, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import EditEntityDialog from '../components/EditEntityDialog';
import RowActions from '../components/RowActions';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function Contacts() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newC, setNewC] = useState({ name: '', category: 'general', phone: '', email: '', role_title: '', notes: '', is_emergency: false });

  useEffect(() => { fetchData(); }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = filter !== 'all' ? `?category=${filter}` : '';
      const r = await axios.get(`${API}/contacts${q}`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/contacts`, newC, { withCredentials: true });
      toast.success('Contact added');
      setShowNew(false);
      setNewC({ name: '', category: 'general', phone: '', email: '', role_title: '', notes: '', is_emergency: false });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete contact?')) return;
    try { await axios.delete(`${API}/contacts/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const emergency = items.filter(c => c.is_emergency || c.category === 'emergency');

  return (
    <Layout>
      <div data-testid="contacts-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('contact.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('contact.subtitle')}</p>
            <p className="text-sm text-muted-foreground">Suppliers, staff, tenants, emergency & authority contacts</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-contact-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />Add Contact</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Name</Label><Input data-testid="contact-name-input" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={newC.category} onValueChange={(v) => setNewC({ ...newC, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                          <SelectItem value="supplier">Supplier</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="tenant">Tenant</SelectItem>
                          <SelectItem value="authority">Authority</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Role / Title</Label><Input value={newC.role_title} onChange={(e) => setNewC({ ...newC, role_title: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone</Label><Input value={newC.phone} onChange={(e) => setNewC({ ...newC, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input type="email" value={newC.email} onChange={(e) => setNewC({ ...newC, email: e.target.value })} /></div>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={newC.notes} onChange={(e) => setNewC({ ...newC, notes: e.target.value })} /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newC.is_emergency} onChange={(e) => setNewC({ ...newC, is_emergency: e.target.checked })} /> Emergency Contact</label>
                  <Button data-testid="submit-contact-btn" onClick={handleCreate} disabled={!newC.name} className="w-full bg-teal-600 hover:bg-teal-700">Add Contact</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {emergency.length > 0 && (
          <Card className="p-4 border-l-4 border-l-red-500 bg-red-50">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-900">Emergency Contacts</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {emergency.map(c => (
                <a key={c.id} href={`tel:${c.phone}`} className="p-3 rounded-lg bg-white hover:bg-red-100 transition">
                  <div className="font-semibold text-foreground text-sm">{c.name}</div>
                  <div className="text-xs text-red-700 font-medium">{c.phone}</div>
                </a>
              ))}
            </div>
          </Card>
        )}

        <div className="flex gap-2 flex-wrap">
          {['all', 'emergency', 'supplier', 'staff', 'tenant', 'authority', 'general'].map(f => (
            <button key={f} data-testid={`filter-contact-${f}`} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === f ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>{f}</button>
          ))}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map(c => (
              <Card key={c.id} data-testid={`contact-card-${c.id}`} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-foreground">{c.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{c.role_title || c.category}</div>
                  </div>
                  {MANAGE.includes(user?.role) && (
                    <RowActions
                      testIdPrefix={`contact-${c.id}`}
                      onEdit={() => setEditingId(c.id)}
                      onDelete={() => handleDelete(c.id)}
                      deleteLabel={`contact "${c.name}"`}
                    />
                  )}
                </div>
                {c.phone && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone className="w-3.5 h-3.5" /><a href={`tel:${c.phone}`} className="hover:text-teal-600">{c.phone}</a></div>}
                {c.email && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Mail className="w-3.5 h-3.5" /><a href={`mailto:${c.email}`} className="hover:text-teal-600 truncate">{c.email}</a></div>}
                {c.notes && <div className="text-xs text-muted-foreground mt-2">{c.notes}</div>}
              </Card>
            ))}
            {items.length === 0 && <Card className="p-10 text-center col-span-full"><p className="text-muted-foreground">No contacts</p></Card>}
          </div>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={items.find(x => x.id === editingId)}
            endpoint="contacts"
            title="Edit Contact"
            onSaved={() => { setEditingId(null); fetchData(); }}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'role_title', label: 'Role / Title' },
              { key: 'category', label: 'Category', type: 'select', options: [
                { value: 'general', label: 'General' },
                { value: 'emergency', label: 'Emergency' },
                { value: 'supplier', label: 'Supplier' },
                { value: 'staff', label: 'Staff' },
                { value: 'tenant', label: 'Tenant' },
                { value: 'authority', label: 'Authority' },
              ]},
              { key: 'phone', label: 'Phone', type: 'tel' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
            ]}
          />
        )}
      </div>
    </Layout>
  );
}
